import * as React from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { rootRoute } from './__root';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const ForgotPasswordFormSchema = z.object({
  employeeId: z.string().trim().min(1, 'Employee ID is required'),
});
type FormValues = z.infer<typeof ForgotPasswordFormSchema>;

function ForgotPasswordPage(): JSX.Element {
  const [sent, setSent] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(ForgotPasswordFormSchema),
    defaultValues: { employeeId: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      api.forgotPassword({ employeeId: values.employeeId.trim().toUpperCase() }),
    onSuccess: () => setSent(true),
    onError: (err) => {
      setErrorMessage(
        toMessage(err),
      );
    },
  });

  const onSubmit = (values: FormValues): void => {
    setErrorMessage(null);
    mutation.mutate(values);
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-lg items-center px-4">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Reset your password</CardTitle>
          <CardDescription>
            Enter your employee ID and we will email you a link to choose a new password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="flex flex-col gap-4">
              <Alert>
                <AlertTitle>Check your email</AlertTitle>
                <AlertDescription>
                  If that employee ID belongs to an active account, a reset link is on its way. The
                  link expires in 30 minutes.
                </AlertDescription>
              </Alert>
              <Link to="/login" className="text-sm underline">
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              {errorMessage ? (
                <Alert variant="destructive" className="mb-4">
                  <AlertTitle>Could not send the link</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              ) : null}

              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="employeeId">Employee ID</Label>
                  <Input
                    id="employeeId"
                    type="text"
                    autoComplete="username"
                    autoCapitalize="characters"
                    placeholder="BITS260001"
                    {...form.register('employeeId')}
                  />
                  {form.formState.errors.employeeId ? (
                    <p className="text-xs text-[hsl(var(--destructive))]">
                      {form.formState.errors.employeeId.message}
                    </p>
                  ) : null}
                </div>

                <Button type="submit" disabled={mutation.isPending}>
                  {mutation.isPending ? 'Sending…' : 'Email me a reset link'}
                </Button>

                <Link to="/login" className="text-sm underline">
                  Back to sign in
                </Link>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
